import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    FiHash, FiCalendar, FiArrowRight, FiAlertCircle,
    FiShield, FiSmartphone, FiCreditCard,
} from 'react-icons/fi';
import ReCAPTCHA from 'react-google-recaptcha';
import Header from '../../components/Header';
import FooterHome from '../Home2/components/FooterHome';
import { validarPorCodigoApi } from '../../Api';
import { RECAPTCHA_SITE_KEY } from '../../config/recaptcha';

// Sem site key, o widget não é exigido (dev): o backend também pula a
// verificação quando a RECAPTCHA_SECRET não está setada.

export default function ValidarAcesso() {
    const navigate = useNavigate();
    const recaptchaRef = useRef(null);
    const [codigoUso, setCodigoUso] = useState('');
    const [dataNascimento, setDataNascimento] = useState('');
    const [hp, setHp] = useState(''); // honeypot — humano deixa vazio
    const [captchaToken, setCaptchaToken] = useState('');
    const [erro, setErro] = useState('');
    const [loading, setLoading] = useState(false);

    const resetCaptcha = () => {
        setCaptchaToken('');
        try { recaptchaRef.current?.reset(); } catch (e) { /* noop */ }
    };

    const submeter = async (e) => {
        e.preventDefault();
        setErro('');

        if (!codigoUso.trim()) return setErro('Informe o código de uso.');
        if (!dataNascimento) return setErro('Informe a data de nascimento.');
        // Só exige o captcha quando a site key existe (em dev sem chave, libera).
        if (RECAPTCHA_SITE_KEY && !captchaToken) return setErro('Confirme que você não é um robô.');

        setLoading(true);
        try {
            const res = await validarPorCodigoApi({
                codigoUso: codigoUso.trim(),
                dataNascimento,
                captchaToken,
                hp,
            });
            const cpf = String(res.data?.cpf || '').replace(/\D/g, '');
            if (!cpf) {
                setErro('Não foi possível validar. Tente novamente.');
                resetCaptcha();
                return;
            }
            // validado:true → o /validar/:cpf não pede captcha de novo (já passou aqui).
            navigate(`/validar/${cpf}`, { state: { validado: true } });
        } catch (err) {
            const msg = err?.response?.data?.message || 'Não foi possível validar. Confira os dados e tente de novo.';
            setErro(msg);
            // Token do reCAPTCHA é de uso único — reseta após cada tentativa enviada.
            resetCaptcha();
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={styles.wrap}>
            <Header />

            <section style={styles.hero}>
                <div style={styles.heroInner}>
                    <header style={styles.heroHead}>
                        <h1 style={styles.heroTitle}>
                            Valide a Carteira de Identificação Estudantil
                        </h1>
                        <div style={styles.heroRule} />
                    </header>

                    <div style={styles.cols}>
                        {/* Coluna esquerda: formulário */}
                        <div style={styles.card}>
                            <form onSubmit={submeter} noValidate>
                                <div style={styles.field}>
                                    <label style={styles.label}><FiHash size={13} /> Código de uso</label>
                                    <input
                                        value={codigoUso}
                                        onChange={(e) => setCodigoUso(e.target.value)}
                                        style={styles.input}
                                        placeholder="Código de uso"
                                        disabled={loading}
                                        autoComplete="off"
                                    />
                                </div>

                                <div style={styles.field}>
                                    <label style={styles.label}><FiCalendar size={13} /> Data de Nascimento</label>
                                    <input
                                        type="date"
                                        value={dataNascimento}
                                        onChange={(e) => setDataNascimento(e.target.value)}
                                        style={styles.input}
                                        disabled={loading}
                                    />
                                </div>

                                {/* Honeypot — invisível pra humano; bot tende a preencher. */}
                                <input
                                    type="text"
                                    value={hp}
                                    onChange={(e) => setHp(e.target.value)}
                                    style={styles.honeypot}
                                    tabIndex={-1}
                                    autoComplete="off"
                                    aria-hidden="true"
                                />

                                {RECAPTCHA_SITE_KEY ? (
                                    <div style={styles.captchaWrap}>
                                        <ReCAPTCHA
                                            ref={recaptchaRef}
                                            sitekey={RECAPTCHA_SITE_KEY}
                                            onChange={(token) => setCaptchaToken(token || '')}
                                            onExpired={() => setCaptchaToken('')}
                                            onErrored={() => setCaptchaToken('')}
                                            hl="pt-BR"
                                        />
                                    </div>
                                ) : (
                                    <div style={styles.captchaAviso}>
                                        <FiShield size={14} /> Verificação anti-robô desativada (sem chave configurada).
                                    </div>
                                )}

                                {erro && (
                                    <div style={styles.erro}>
                                        <FiAlertCircle size={15} /> {erro}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={loading}
                                    style={{ ...styles.btn, opacity: loading ? 0.65 : 1 }}
                                >
                                    {loading ? (
                                        <><span style={styles.btnSpinner} /> Validando…</>
                                    ) : (
                                        <>Validar <FiArrowRight size={16} /></>
                                    )}
                                </button>
                            </form>
                        </div>

                        {/* Coluna direita: explicação */}
                        <div style={styles.info}>
                            <p style={styles.infoLead}>
                                Aqui você confirma, na hora, se uma carteirinha de estudante da UEN
                                é verdadeira e está dentro da validade. Há duas maneiras de checar:
                            </p>
                            <ul style={styles.infoList}>
                                <li style={styles.infoItem}>
                                    <b>Pelo formulário ao lado:</b> digite o <b>código de uso</b> impresso
                                    na carteirinha junto com a <b>data de nascimento</b> do estudante. Os
                                    dois precisam corresponder ao mesmo cadastro pra liberar a consulta.
                                </li>
                                <li style={styles.infoItem}>
                                    <b>Pelo QR-Code:</b> aponte a câmera do celular para o código impresso
                                    na carteirinha. Ele abre direto esta validação, já com os dados do
                                    titular carregados — sem precisar digitar nada.
                                </li>
                            </ul>
                            <p style={styles.infoNote}>
                                Na via impressa, vale conferir também alguns detalhes de segurança:
                            </p>
                            <ul style={styles.subList}>
                                <li style={styles.subItem}><FiCreditCard size={13} /> Cartão em PVC rígido, com acabamento e corte uniformes</li>
                                <li style={styles.subItem}><FiShield size={13} /> Fundo com tramas finas que dificultam cópia e digitalização</li> 
                            </ul> 
                        </div>
                    </div>
                </div>
            </section>

            <FooterHome />

            {/* Animações locais (spin + pop do check). */}
            <style>{`
                @keyframes va-spin { to { transform: rotate(360deg); } }
                @keyframes va-pop { 0% { transform: scale(0.4); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }
            `}</style>
        </div>
    );
}

const styles = {
    wrap: {
        minHeight: '100vh', display: 'flex', flexDirection: 'column',
        fontFamily: "'Inter', 'Roboto', system-ui, sans-serif", color: '#0F172A',
        background: '#F8FAFC',
    },
    hero: {
        flex: 1,
        background: 'linear-gradient(135deg, #047857 0%, #0D9488 55%, #059669 100%)',
        padding: '48px 20px 64px',
    },
    heroInner: { maxWidth: 1120, margin: '0 auto' },
    heroHead: { marginBottom: 36 },
    heroTitle: {
        color: '#FFFFFF', fontSize: 'clamp(1.5rem, 3vw, 2.2rem)', fontWeight: 800,
        margin: 0, lineHeight: 1.2, maxWidth: 620,
    },
    heroRule: {
        width: 220, height: 4, background: 'rgba(255,255,255,0.85)',
        borderRadius: 2, marginTop: 16,
    },
    cols: {
        display: 'grid', gridTemplateColumns: 'minmax(300px, 420px) 1fr',
        gap: 36, alignItems: 'start',
    },
    card: {
        background: '#FFFFFF', borderRadius: 16, padding: '28px 26px',
        boxShadow: '0 24px 48px -16px rgba(2,44,34,0.35), 0 0 0 1px rgba(15,23,42,0.04)',
    },
    field: { marginBottom: 16 },
    label: {
        display: 'inline-flex', alignItems: 'center', gap: 6,
        fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: 6,
    },
    input: {
        width: '100%', padding: '12px 13px', border: '1.5px solid #E2E8F0',
        borderRadius: 10, fontSize: '0.95rem', color: '#0F172A', background: '#fff',
        outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
    },
    honeypot: {
        position: 'absolute', left: '-9999px', top: 'auto',
        width: 1, height: 1, opacity: 0, pointerEvents: 'none',
    },
    captchaWrap: { marginTop: 4, display: 'flex', justifyContent: 'flex-start' },
    captchaAviso: {
        marginTop: 4, fontSize: '0.78rem', color: '#92400E',
        background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8,
        padding: '9px 11px', display: 'flex', alignItems: 'center', gap: 7,
    },
    erro: {
        marginTop: 14, fontSize: '0.84rem', color: '#9F1239',
        background: '#FFF1F2', padding: '10px 12px', border: '1px solid #FECDD3',
        borderRadius: 8, display: 'flex', alignItems: 'center', gap: 7,
    },
    btn: {
        width: '100%', padding: '13px', marginTop: 18,
        background: 'linear-gradient(135deg, #059669, #10B981)', color: '#fff',
        border: 'none', borderRadius: 10, fontSize: '0.98rem', fontWeight: 700,
        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        fontFamily: 'inherit', boxShadow: '0 4px 12px -2px rgba(5,150,105,0.4)',
    },
    btnSpinner: {
        width: 16, height: 16, border: '2px solid rgba(255,255,255,0.4)',
        borderTopColor: '#fff', borderRadius: '50%',
        animation: 'va-spin 0.8s linear infinite',
    },
    info: { color: '#F0FDFA', paddingTop: 4 },
    infoLead: { fontSize: '1.02rem', lineHeight: 1.6, margin: '0 0 18px', color: '#ECFDF5' },
    infoList: { margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 14 },
    infoItem: { fontSize: '0.96rem', lineHeight: 1.6, color: '#E2F5EF' },
    infoNote: { fontSize: '0.95rem', lineHeight: 1.6, margin: '20px 0 10px', color: '#ECFDF5', fontWeight: 600 },
    subList: { margin: '0', paddingLeft: 4, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 },
    subItem: { display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9rem', color: '#D1FAE5' }, 
};
